import React, {useEffect, useState} from "react";
import {inject, observer} from 'mobx-react';
import Modal from '../../components/modal';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faPencilAlt,
    faPlus,
    faSpinner,
    faWindowClose,
} from "@fortawesome/free-solid-svg-icons";
import Select from 'react-select';
import Checkbox from "../../components/checkbox";
import {fetch} from '../../utils/api-wrapper';
import HoverInfoCard from "../../components/hover-card";
import Switch from "react-switch";
import {
    applyRateInputsToListRates,
    buildRateInputsFromListRates,
    DEFAULT_RATE_INPUTS,
    formatRateInputOnBlur,
    prepareRateInputForEditing,
    sanitizeRatePercentTyping,
} from "./admin-tax-codes-rate-utils";

const getRateInputProps = (rateInputs, setRateInputs, fieldKey) => ({
    type: "text",
    className: "form-control",
    inputMode: "decimal",
    placeholder: "0.000",
    value: rateInputs[fieldKey],
    onChange: (e) => setRateInputs({
        ...rateInputs,
        [fieldKey]: sanitizeRatePercentTyping(e.target.value),
    }),
    onFocus: () => {
        const editableValue = prepareRateInputForEditing(rateInputs[fieldKey]);
        if (editableValue !== rateInputs[fieldKey]) {
            setRateInputs({
                ...rateInputs,
                [fieldKey]: editableValue,
            });
        }
    },
    onBlur: () => setRateInputs((prev) => ({
        ...prev,
        [fieldKey]: formatRateInputOnBlur(prev[fieldKey]),
    })),
    required: true,
});


const AdminTaxCodes = ({data}) => {
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [client, setClient] = useState(null);
    const [clients, setClients] = useState(null);
    const [taxCodes, setTaxCodes] = useState([]);
    const [showUpdateTaxCodeModal, setShowUpdateTaxCodeModal] = useState(false);
    const [updatedTaxCodeData, setUpdatedTaxCodeData] = useState({});
    const [taxCodesDeleteSelection, setTaxCodesDeleteSelection] = useState([]);
    const [showDeleteTaxCodeModal, setShowDeleteTaxCodeModal] = useState(false);
    const [showNewTaxCodeModal, setShowNewTaxCodeModal] = useState(false);
    const [newTaxCodeData, setNewTaxCodeData] = useState({
        "code": "",
        "description": "",
        "list_regex_must_contain": [
            [],
            []
        ],
        "list_rates": [
            0,
            0
        ],
        "_view_id": 0
    });
    const [editRateInputs, setEditRateInputs] = useState(DEFAULT_RATE_INPUTS);
    const [newRateInputs, setNewRateInputs] = useState(DEFAULT_RATE_INPUTS);

    const [pageSize, setPageSize] = useState(10);
    const [filterValue, setFilterValue] = useState("");
    const [sorting, setSorting] = useState([{"id": "names", "desc": false}]);
    const [page, setPage] = useState(1);
    const [totalRowCount, setTotalRowCount] = useState(0);

    useEffect(() => {
        setLoading(true);
        refreshClients()
    }, []);

    useEffect(() => {
        if (client) {
            refreshTaxCodes();
        }
    }, [client]);

    const refreshClients = () => {
        fetch("/clients/", "GET", {}, response => {
                const clients = response.data.clients;
                setClients(clients);
                
                const current_client = (clients.find(c => c._id === data.id_client))
                selectClient(current_client);
                setLoading(false);
            },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    }

    const selectClient = (client) => {
        setClient({...client})
        setPage(1)
        setFilterValue("");
    }
    
    const updateTaxCodes = (clientId = client._id, updatedTaxCode = updatedTaxCodeData) => {
        let updatedTaxProfiles = [...taxCodes]

        updatedTaxProfiles.forEach((item, i, arr) => {
            if (item._view_id === updatedTaxCode._view_id) {
                arr[i] = { ...updatedTaxCode };
            }
            delete arr[i]._view_id;
        });
        

        fetch("/clients/" + clientId + "/tax-profile", "PUT", updatedTaxProfiles, response => {
                refreshTaxCodes()
                setLoading(false);
            },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    }

    // Form submission handler
    const updateTaxCodeSubmit = (e) => {
        e.preventDefault();
        setShowUpdateTaxCodeModal(false);
        updateTaxCodes(client._id, {
            ...updatedTaxCodeData,
            list_rates: applyRateInputsToListRates(updatedTaxCodeData.list_rates, editRateInputs),
        });
    };

    const refreshTaxCodes = () => {
        let client_id = client._id;

        fetch("/clients/" + client_id + "/tax-profile", "GET", {}, response => {
                const tax_profile = response.data.tax_profile;
                tax_profile.forEach((item, i) => {
                    item._view_id = i
                    item.list_regex_must_contain = [
                        (item.list_regex_must_contain?.[0]) ? item.list_regex_must_contain[0] : [],
                        (item.list_regex_must_contain?.[1]) ? item.list_regex_must_contain[1] : [],
                    ];
                })
                setTaxCodes(tax_profile);
                setLoading(false);
            },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    }

    const openUpdateTaxCodeModal = (selectedTaxCode) => {
        setUpdatedTaxCodeData(selectedTaxCode);
        setEditRateInputs(buildRateInputsFromListRates(selectedTaxCode.list_rates));
        setShowUpdateTaxCodeModal(true);
    }

    const openNewTaxCodeModal = () => {
        setNewTaxCodeData({
            code: "",
            description: "",
            list_regex_must_contain: [[], []],
            list_rates: [0, 0],
            _view_id: taxCodes.length,
        });
        setNewRateInputs(DEFAULT_RATE_INPUTS);
        setShowNewTaxCodeModal(true);
    }

    const cancelUpdateTaxCodeModal = () => {
        setShowUpdateTaxCodeModal(false);
    }

    const createTaxCode = (clientId = client._id, newTaxCode = newTaxCodeData) => {
        let updatedTaxProfiles = [...taxCodes, newTaxCode]

        updatedTaxProfiles.forEach((item, i, arr) => {
            delete arr[i]._view_id;
        });
        

        fetch("/clients/" + clientId + "/tax-profile", "PUT", updatedTaxProfiles, response => {
                refreshTaxCodes()
                setLoading(false);
            },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    };

    const newTaxCodeSubmit = (e) => {
        e.preventDefault();
        setShowNewTaxCodeModal(false);
        createTaxCode(client._id, {
            ...newTaxCodeData,
            list_rates: applyRateInputsToListRates(newTaxCodeData.list_rates, newRateInputs),
        });
    };

    const cancelNewTaxCodeModal = () => {
        setShowNewTaxCodeModal(false);
    }

    const cancelDeleteTaxCode = () => {
        setShowDeleteTaxCodeModal(false);
        setTaxCodesDeleteSelection([])
    }

    const confirmDeleteTaxCode = () => {
        deleteTaxCodes()
        setShowDeleteTaxCodeModal(false);
    }

    const deleteTaxCodes = (clientId = client._id, updatedTaxCode = updatedTaxCodeData) => {
        let updatedTaxProfiles = taxCodes.filter((item, i) => !isTaxCodeSelected(item))
        fetch("/clients/" + clientId + "/tax-profile", "PUT", updatedTaxProfiles, response => {
                refreshTaxCodes()
                setLoading(false);
            },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    }

 
    
    const isAllTaxCodesSelected = () => {
        if (!taxCodes || !taxCodes.length) {
            return false;
        }
        return taxCodes.every(taxCode => taxCodesDeleteSelection.includes(taxCode._view_id));
    }

    const isTaxCodeSelected = (taxCode) => {
        return taxCodesDeleteSelection.includes(taxCode._view_id);
    }

    const toggleAllTaxCodesSelected = () => {
        setTaxCodesDeleteSelection(isAllTaxCodesSelected() ?
            taxCodesDeleteSelection.filter(id => !taxCodes.some(taxCode => taxCode._view_id === id))
            :
            [...taxCodesDeleteSelection, ...taxCodes.map((taxCode) => taxCode._view_id).filter((id) => !taxCodesDeleteSelection.includes(id))]
        );
    }

    // Common styles for react-select components.
    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "0 !important",
            boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important"
        })
    };

    //pagination logic
    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = indexOfFirstItem + taxCodes.length - 1;
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const emptyRowsCount = Math.max(0, pageSize - taxCodes.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    return !loading ?
                    <div>
                        <div>
                            <div className="d-flex justify-content-between align-items-center px-4 py-2">
                                <div className="flex mr-3">
                                    <Select
                                        name="client"
                                        isDisabled={data.role !== "SuperAdmin"}
                                        value={client === null ? null : {value: client, label: client.name}}
                                        options={clients.map(client => ({value: client, label: client.name}))}
                                        styles={styleSelect}
                                        classNamePrefix="select"
                                        onChange={(choice) => {
                                            selectClient(choice.value);
                                        }}
                                    />
                                </div>
                                <div className="d-flex">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm ml-2"
                                        disabled={taxCodesDeleteSelection.length === 0}
                                        onClick={() => setShowDeleteTaxCodeModal(true)}>
                                        {data.t.read("delete") + " (" + taxCodesDeleteSelection.length + ")"}
                                    </button>
                                    <button
                                        className='btn btn-primary btn-sm ml-2 justify-content-end'
                                        type="button"
                                        onClick={openNewTaxCodeModal}
                                    >
                                        {data.t.read("add")}
                                    </button>
                                </div>
                            </div>
                            <div className="mb-2 card rounded-lg admin-table">
                                <table className='table table-hover font-small'>
                                    <thead>
                                    <tr>
                                        <th>
                                            <Checkbox disabled={!taxCodes || !taxCodes.length}
                                                      isChecked={isAllTaxCodesSelected()}
                                                      onChange={toggleAllTaxCodesSelected}/>
                                        </th>
                                        <th className="col-2" >
                                            {"Code"}
                                        </th>
                                        <th className="col-2" >
                                            {`${data.t.read("tax_1_rate")}`}
                                        </th>
                                        <th className="col-2" >
                                            {`${data.t.read("tax_1_components")}`}
                                        </th>
                                        <th className="col-2" >
                                            {`${data.t.read("tax_2_rate")}`}
                                        </th>
                                        <th className="col-2" >
                                            {`${data.t.read("tax_2_components")}`}
                                        </th>
                                        <th className="">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {
                                        taxCodes.map((taxCode, index) => {
                                            return <tr key={index}>
                                                <td>
                                                    <Checkbox isChecked={isTaxCodeSelected(taxCode)}
                                                              onChange={() => isTaxCodeSelected(taxCode) ? setTaxCodesDeleteSelection(taxCodesDeleteSelection.filter(x => x !== taxCode._view_id)) : setTaxCodesDeleteSelection([...taxCodesDeleteSelection, taxCode._view_id])}/>
                                                </td>
                                                <td className={"col-2"} onClick={()=>openUpdateTaxCodeModal(taxCode)}>
                                                    {taxCode.code}
                                                </td>
                                                <td className={"col-2"} onClick={()=>openUpdateTaxCodeModal(taxCode)}>
                                                    {`${((taxCode.list_rates[0] ?? 0)*100).toFixed(3)}%`}
                                                </td>
                                                <td className={"col-2"} onClick={()=>openUpdateTaxCodeModal(taxCode)}>
                                                    <HoverInfoCard info={taxCode.list_regex_must_contain?.[0] ? taxCode.list_regex_must_contain?.[0].join(', ') : ''} />
                                                </td>
                                                <td className={"col-2"} onClick={()=>openUpdateTaxCodeModal(taxCode)}>
                                                    {`${((taxCode.list_rates[1] ?? 0)*100).toFixed(3)}%`}
                                                </td>
                                                <td className={"col-2"} onClick={()=>openUpdateTaxCodeModal(taxCode)}>
                                                    <HoverInfoCard info={taxCode.list_regex_must_contain?.[1] ? taxCode.list_regex_must_contain?.[1].join(', ') : ''} />
                                                </td>
                                                <td className="col-1">
                                                    {<FontAwesomeIcon className="mr-2" icon={faPencilAlt}
                                                                      onClick={() =>openUpdateTaxCodeModal(taxCode)}
                                                                      type="button"/>}
                                                    {<FontAwesomeIcon onClick={() => {
                                                        setTaxCodesDeleteSelection([taxCode._view_id])
                                                        setShowDeleteTaxCodeModal(true)
                                                    }}
                                                                      icon={faWindowClose} type="button"
                                                                      style={{color: 'red'}}/>}
                                                </td>

                                            </tr>
                                        })
                                    }
                                    {
                                        emptyRows.map((_, idx) => (
                                            <tr key={`empty-${idx}`}>
                                                <td colSpan="7">&nbsp;</td>
                                            </tr>
                                        ))
                                    }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {
                            showUpdateTaxCodeModal && (
                                <Modal title={data.t.read("tax")}>
                                    <form onSubmit={updateTaxCodeSubmit}>
                                        <div className={"form-group"}>
                                            <label htmlFor={"code"}>{"Code"}</label>
                                            <input type={"text"} className={"form-control"} name={"code"}
                                                   value={updatedTaxCodeData.code}
                                                   onChange={(e) => setUpdatedTaxCodeData({
                                                       ...updatedTaxCodeData,
                                                       code: e.target.value,
                                                   })}
                                                   required
                                            />
                                        </div>
                                        <div className={"form-group"}>
                                            <label htmlFor={"description"}>{data.t.read("description")}</label>
                                            <textarea
                                                className={"form-control"}
                                                name={"description"}
                                                rows={3}
                                                value={updatedTaxCodeData.description ?? ""}
                                                onChange={(e) => setUpdatedTaxCodeData({
                                                    ...updatedTaxCodeData,
                                                    description: e.target.value,
                                                })}
                                            />
                                        </div>
                                        <div className={"form-group"}>
                                            <label htmlFor={"code"}>{data.t.read("default")}</label>
                                            <div className={"d-flex  align-items-center"}>
                                                <Switch
                                                    className='switch mt-0  '
                                                    checked={updatedTaxCodeData.is_default}
                                                    width={70}
                                                    onColor="#F39800"
                                                    onHandleColor="#2693e6"
                                                    boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                    activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                    onChange={(active) => setUpdatedTaxCodeData({
                                                        ...updatedTaxCodeData,
                                                        is_default: active
                                                    })}
                                                    
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label> {data.t.read("tax")} 1</label>
                                            <div className={"form-group"}>
                                                <h5 >{data.t.read("rate")} (%)</h5>
                                                <input name={"tax1 rate"} {...getRateInputProps(editRateInputs, setEditRateInputs, "tax1")} />
                                            </div>
                                            <div className={"form-group"}>
                                                <h5>
                                                    {data.t.read("components")}
                                                    <button className='btn btn-primary btn-xs ml-2 pl-1 pr-1' type="button"
                                                            onClick={() => setUpdatedTaxCodeData({
                                                                ...updatedTaxCodeData,
                                                                list_regex_must_contain: updatedTaxCodeData.list_regex_must_contain.map((item, index) => index === 0 ? [...updatedTaxCodeData.list_regex_must_contain[0], ""] : item),
                                                            })}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus}/>
                                                    </button>

                                                </h5>
                                                {
                                                    updatedTaxCodeData.list_regex_must_contain?.[0].map((itemValue, itemIdx) => (
                                                        <div
                                                            key={itemIdx}
                                                            className="d-flex justify-content-between form-control align-items-center mb-2 p-2"
                                                        >
                                                            <input
                                                                type="text"
                                                                required
                                                                className="form-control border-0"
                                                                style={{ backgroundColor: "transparent" }}
                                                                value={itemValue}
                                                                onChange={e =>
                                                                    setUpdatedTaxCodeData({
                                                                        ...updatedTaxCodeData,
                                                                        list_regex_must_contain: updatedTaxCodeData.list_regex_must_contain.map((item, index) => index === 0 ?
                                                                            updatedTaxCodeData.list_regex_must_contain[0].map((code, x)=> x === itemIdx ? e.target.value : code)
                                                                            : item),
                                                                    })}
                                                            />

                                                            <FontAwesomeIcon
                                                                className="ml-2 align-content-center"
                                                                icon={faWindowClose}
                                                                type="button"
                                                                color="red"
                                                                onClick={() =>
                                                                    setUpdatedTaxCodeData({...updatedTaxCodeData, list_regex_must_contain: updatedTaxCodeData.list_regex_must_contain.map((item, index) => index === 0 ?
                                                                            updatedTaxCodeData.list_regex_must_contain[0].filter((_, j) => j !== itemIdx) : item)})
                                                                }
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>

                                        <div>
                                            <label> {data.t.read("tax")} 2</label>
                                            <div className={"form-group"}>
                                                <h5 >{data.t.read("rate")} (%)</h5>
                                                <input name={"tax2 rate"} {...getRateInputProps(editRateInputs, setEditRateInputs, "tax2")} />
                                            </div>
                                            <div className={"form-group"}>
                                                <h5>
                                                    {data.t.read("components")}
                                                    <button className='btn btn-primary btn-xs ml-2 pl-1 pr-1' type="button"
                                                            onClick={() => setUpdatedTaxCodeData({
                                                                ...updatedTaxCodeData,
                                                                list_regex_must_contain: updatedTaxCodeData.list_regex_must_contain.map((item, index) => index === 1 ? [...updatedTaxCodeData.list_regex_must_contain[1], ""] : item),
                                                            })}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus}/>
                                                    </button>

                                                </h5>
                                                {
                                                    updatedTaxCodeData.list_regex_must_contain?.[1].map((itemValue, itemIdx) => (
                                                        <div
                                                            key={itemIdx}
                                                            className="d-flex justify-content-between form-control align-items-center mb-2 p-2"
                                                        >
                                                            <input
                                                                type="text"
                                                                required
                                                                className="form-control border-0"
                                                                style={{ backgroundColor: "transparent" }}
                                                                value={itemValue}
                                                                onChange={e =>
                                                                    setUpdatedTaxCodeData({
                                                                        ...updatedTaxCodeData,
                                                                        list_regex_must_contain: updatedTaxCodeData.list_regex_must_contain.map((item, index) => index === 1 ?
                                                                            updatedTaxCodeData.list_regex_must_contain[1].map((code, x)=> x === itemIdx ? e.target.value : code)
                                                                            : item),
                                                                    })}
                                                            />

                                                            <FontAwesomeIcon
                                                                className="ml-2 align-content-center"
                                                                icon={faWindowClose}
                                                                type="button"
                                                                color="red"
                                                                onClick={() =>
                                                                    setUpdatedTaxCodeData({...updatedTaxCodeData, list_regex_must_contain: updatedTaxCodeData.list_regex_must_contain.map((item, index) => index === 1 ?
                                                                            updatedTaxCodeData.list_regex_must_contain[1].filter((_, j) => j !== itemIdx) : item)})
                                                                }
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    
                                        <span className="d-flex justify-content-between">
                                        <button type="button" className={"btn btn-outline-dark"}
                                                onClick={cancelUpdateTaxCodeModal}>
                                            {data.t.read("cancel")}
                                        </button>
                                        <button type="submit" className="btn btn-primary" b>
                                             {data.t.read("save")}
                                        </button>
                                    </span>
                                    </form>
                                    
                                </Modal>
                            )
                        }
                        {
                            showNewTaxCodeModal && (
                                <Modal title={data.t.read("tax")}>
                                    <form onSubmit={newTaxCodeSubmit}>
                                        <div className={"form-group"}>
                                            <label htmlFor={"code"}>{"Code"}</label>
                                            <input type={"text"} className={"form-control"} name={"code"}
                                                   value={newTaxCodeData.code}
                                                   onChange={(e) => setNewTaxCodeData({
                                                       ...newTaxCodeData,
                                                       code: e.target.value,
                                                   })}
                                                   required
                                            />

                                        </div>
                                        <div className={"form-group"}>
                                            <label htmlFor={"description"}>{data.t.read("description")}</label>
                                            <textarea
                                                className={"form-control"}
                                                name={"description"}
                                                rows={3}
                                                value={newTaxCodeData.description ?? ""}
                                                onChange={(e) => setNewTaxCodeData({
                                                    ...newTaxCodeData,
                                                    description: e.target.value,
                                                })}
                                            />
                                        </div>
                                        <div className={"form-group"}>
                                            <label htmlFor={"code"}>{data.t.read("default")}</label>
                                            <div className={"d-flex  align-items-center"}>
                                                <Switch
                                                    className='switch mt-0  '
                                                    checked={newTaxCodeData.is_default}
                                                    width={70}
                                                    onColor="#F39800"
                                                    onHandleColor="#2693e6"
                                                    boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                    activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                    onChange={(active) => setNewTaxCodeData({
                                                        ...newTaxCodeData,
                                                        is_default: active
                                                    })}

                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label> {data.t.read("tax")} 1</label>
                                            <div className={"form-group"}>
                                                <h5 >{data.t.read("rate")} (%)</h5>
                                                <input name={"tax1 rate"} {...getRateInputProps(newRateInputs, setNewRateInputs, "tax1")} />

                                            </div>
                                            <div className={"form-group"}>
                                                <h5>
                                                    {data.t.read("components")}
                                                    <button className='btn btn-primary btn-xs ml-2 pl-1 pr-1' type="button"
                                                            onClick={() => setNewTaxCodeData({
                                                                ...newTaxCodeData,
                                                                list_regex_must_contain: newTaxCodeData.list_regex_must_contain.map((item, index) => index === 0 ? [...newTaxCodeData.list_regex_must_contain[0], ""] : item),
                                                            })}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus}/>
                                                    </button>

                                                </h5>
                                                {
                                                    newTaxCodeData.list_regex_must_contain?.[0].map((itemValue, itemIdx) => (
                                                        <div
                                                            key={itemIdx}
                                                            className="d-flex justify-content-between form-control align-items-center mb-2 p-2"
                                                        >
                                                            <input
                                                                type="text"
                                                                required
                                                                className="form-control border-0"
                                                                style={{ backgroundColor: "transparent" }}
                                                                value={itemValue}
                                                                onChange={e =>
                                                                    setNewTaxCodeData({
                                                                        ...newTaxCodeData,
                                                                        list_regex_must_contain: newTaxCodeData.list_regex_must_contain.map((item, index) => index === 0 ?
                                                                            newTaxCodeData.list_regex_must_contain[0].map((code, x)=> x === itemIdx ? e.target.value : code)
                                                                            : item),
                                                                    })}
                                                            />

                                                            <FontAwesomeIcon
                                                                className="ml-2 align-content-center"
                                                                icon={faWindowClose}
                                                                type="button"
                                                                color="red"
                                                                onClick={() =>
                                                                    setNewTaxCodeData({...newTaxCodeData, list_regex_must_contain: newTaxCodeData.list_regex_must_contain.map((item, index) => index === 0 ?
                                                                            newTaxCodeData.list_regex_must_contain[0].filter((_, j) => j !== itemIdx) : item)})
                                                                }
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>

                                        <div>
                                            <label> {data.t.read("tax")} 2</label>
                                            <div className={"form-group"}>
                                                <h5 >{data.t.read("rate")} (%)</h5>
                                                <input name={"tax2 rate"} {...getRateInputProps(newRateInputs, setNewRateInputs, "tax2")} />

                                            </div>
                                            <div className={"form-group"}>
                                                <h5>
                                                    {data.t.read("components")}
                                                    <button className='btn btn-primary btn-xs ml-2 pl-1 pr-1' type="button"
                                                            onClick={() => setNewTaxCodeData({
                                                                ...newTaxCodeData,
                                                                list_regex_must_contain: newTaxCodeData.list_regex_must_contain.map((item, index) => index === 1 ? [...newTaxCodeData.list_regex_must_contain[1], ""] : item),
                                                            })}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus}/>
                                                    </button>

                                                </h5>
                                                {
                                                    newTaxCodeData.list_regex_must_contain?.[1].map((itemValue, itemIdx) => (
                                                        <div
                                                            key={itemIdx}
                                                            className="d-flex justify-content-between form-control align-items-center mb-2 p-2"
                                                        >
                                                            <input
                                                                type="text"
                                                                required
                                                                className="form-control border-0"
                                                                style={{ backgroundColor: "transparent" }}
                                                                value={itemValue}
                                                                onChange={e =>
                                                                    setNewTaxCodeData({
                                                                        ...newTaxCodeData,
                                                                        list_regex_must_contain: newTaxCodeData.list_regex_must_contain.map((item, index) => index === 1 ?
                                                                            newTaxCodeData.list_regex_must_contain[1].map((code, x)=> x === itemIdx ? e.target.value : code)
                                                                            : item),
                                                                    })}
                                                            />

                                                            <FontAwesomeIcon
                                                                className="ml-2 align-content-center"
                                                                icon={faWindowClose}
                                                                type="button"
                                                                color="red"
                                                                onClick={() =>
                                                                    setNewTaxCodeData({...newTaxCodeData, list_regex_must_contain: newTaxCodeData.list_regex_must_contain.map((item, index) => index === 1 ?
                                                                            newTaxCodeData.list_regex_must_contain[1].filter((_, j) => j !== itemIdx) : item)})
                                                                }
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>

                                        <span className="d-flex justify-content-between">
                                        <button type="button" className={"btn btn-outline-dark"}
                                                onClick={cancelNewTaxCodeModal}>
                                            {data.t.read("cancel")}
                                        </button>
                                        <button type="submit" className="btn btn-primary" b>
                                             {data.t.read("add")}
                                        </button>
                                    </span>
                                    </form>

                                </Modal>
                            )
                        }
                        {showDeleteTaxCodeModal && (
                            <Modal
                                title={data.t.read("department_deletion")}
                                okAction={confirmDeleteTaxCode}
                                cancelAction={cancelDeleteTaxCode}
                                okText={data.t.read("delete")}
                                cancelText={data.t.read("cancel")}>
                                <p className='text-danger'>{data.t.read("delete")}?</p>
                            </Modal>
                        )}
                    </div>
                    :
                    <div>
                        <div className="d-flex loading-container w-100">
                            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner}/>
                        </div>
                    </div>
}

export default inject('data')(observer(AdminTaxCodes));